import { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    deleteOfficeLocation,
    fetchOfficeLocations,
    geocodeOfficeAddress,
    saveOfficeLocation,
} from "../../../redux/slices/officeLocationsSlice";

const DEFAULT_RADIUS = 200;
const EMPTY_FORM = { id: null, name: "", address: "", latitude: "", longitude: "", radius_meters: DEFAULT_RADIUS };

const ERROR_KEYS = {
    "Address not found.": "address_not_found",
    "The address lookup service is unavailable. Try again or enter the coordinates.": "lookup_unavailable",
    "Latitude and longitude must be provided together.": "coordinates_incomplete",
};

const toNumberOrNull = (value) => (value === "" || value === null ? null : Number(value));

export const mapLink = (office) =>
    `https://www.openstreetmap.org/?mlat=${office.latitude}&mlon=${office.longitude}#map=18/${office.latitude}/${office.longitude}`;

const OfficeLocationsCard = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const offices = useSelector((state) => state.officeLocations.items);
    const isLoading = useSelector((state) => state.officeLocations.isLoading);

    const [form, setForm] = useState(null);
    const [isConverting, setIsConverting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        dispatch(fetchOfficeLocations());
    }, [dispatch]);

    const showError = (error) => {
        const key = ERROR_KEYS[error?.detail] || "save_error";
        toast.error(t(`pages.OrganizationSettings.offices.errors.${key}`));
    };

    const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

    const handleConvert = async () => {
        setIsConverting(true);
        try {
            const { latitude, longitude } = await dispatch(geocodeOfficeAddress(form.address.trim())).unwrap();
            setForm((current) => ({ ...current, latitude, longitude }));
        } catch (error) {
            showError(error);
        } finally {
            setIsConverting(false);
        }
    };

    const handleSave = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        try {
            await dispatch(
                saveOfficeLocation({
                    id: form.id,
                    name: form.name.trim(),
                    address: form.address.trim(),
                    latitude: toNumberOrNull(form.latitude),
                    longitude: toNumberOrNull(form.longitude),
                    radius_meters: Number(form.radius_meters),
                }),
            ).unwrap();
            toast.success(t("pages.OrganizationSettings.offices.saved"));
            setForm(null);
        } catch (error) {
            showError(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (office) => {
        if (!window.confirm(t("pages.OrganizationSettings.offices.confirm_delete", { name: office.name }))) return;
        try {
            await dispatch(deleteOfficeLocation(office.id)).unwrap();
        } catch (error) {
            showError(error);
        }
    };

    const canSave = form && form.name.trim() && form.address.trim() && !isSaving && !isConverting;

    return (
        <Card className="shadow-sm border-0 rounded-4 mb-4">
            <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3 d-flex justify-content-between align-items-center">
                {t("pages.OrganizationSettings.offices.title")}
                {!form && (
                    <Button size="sm" onClick={() => setForm(EMPTY_FORM)}>
                        {t("pages.OrganizationSettings.offices.add")}
                    </Button>
                )}
            </Card.Header>
            <Card.Body className="px-4 py-4">
                <p className="text-muted small">{t("pages.OrganizationSettings.offices.description")}</p>

                {isLoading && <Spinner animation="border" size="sm" />}

                {!isLoading && offices.length === 0 && !form && (
                    <div className="text-muted">{t("pages.OrganizationSettings.offices.empty")}</div>
                )}

                {offices.length > 0 && (
                    <Table responsive size="sm" className="align-middle">
                        <thead>
                            <tr>
                                <th>{t("pages.OrganizationSettings.offices.name")}</th>
                                <th>{t("pages.OrganizationSettings.offices.address")}</th>
                                <th>{t("pages.OrganizationSettings.offices.radius")}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {offices.map((office) => (
                                <tr key={office.id}>
                                    <td className="fw-semibold">{office.name}</td>
                                    <td>
                                        {office.address}{" "}
                                        <a href={mapLink(office)} target="_blank" rel="noopener noreferrer" className="small">
                                            {t("pages.OrganizationSettings.offices.view_map")}
                                        </a>
                                    </td>
                                    <td>{office.radius_meters} m</td>
                                    <td className="text-end text-nowrap">
                                        <Button size="sm" variant="link" onClick={() => setForm({ ...office })} disabled={!!form}>
                                            {t("pages.OrganizationSettings.offices.edit")}
                                        </Button>
                                        <Button size="sm" variant="link" className="text-danger" onClick={() => handleDelete(office)}>
                                            {t("pages.OrganizationSettings.offices.delete")}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}

                {form && (
                    <Form onSubmit={handleSave} className="border rounded-3 p-3 mt-2">
                        <Row className="g-3">
                            <Col md={4}>
                                <Form.Group controlId="office-name">
                                    <Form.Label>{t("pages.OrganizationSettings.offices.name")}</Form.Label>
                                    <Form.Control value={form.name} onChange={update("name")} maxLength={120} required />
                                </Form.Group>
                            </Col>
                            <Col md={8}>
                                <Form.Group controlId="office-address">
                                    <Form.Label>{t("pages.OrganizationSettings.offices.address")}</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control value={form.address} onChange={update("address")} maxLength={500} required />
                                        <Button
                                            variant="outline-primary"
                                            onClick={handleConvert}
                                            disabled={!form.address.trim() || isConverting}
                                            className="text-nowrap"
                                        >
                                            {isConverting ? <Spinner animation="border" size="sm" /> : t("pages.OrganizationSettings.offices.convert")}
                                        </Button>
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group controlId="office-latitude">
                                    <Form.Label>{t("pages.OrganizationSettings.offices.latitude")}</Form.Label>
                                    <Form.Control type="number" step="any" min={-90} max={90} value={form.latitude ?? ""} onChange={update("latitude")} />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group controlId="office-longitude">
                                    <Form.Label>{t("pages.OrganizationSettings.offices.longitude")}</Form.Label>
                                    <Form.Control type="number" step="any" min={-180} max={180} value={form.longitude ?? ""} onChange={update("longitude")} />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group controlId="office-radius">
                                    <Form.Label>{t("pages.OrganizationSettings.offices.radius")}</Form.Label>
                                    <Form.Control type="number" min={50} max={5000} step={10} value={form.radius_meters} onChange={update("radius_meters")} required />
                                </Form.Group>
                            </Col>
                        </Row>
                        <div className="small text-muted mt-2">{t("pages.OrganizationSettings.offices.coordinates_hint")}</div>
                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <Button variant="outline-secondary" onClick={() => setForm(null)} disabled={isSaving}>
                                {t("pages.OrganizationSettings.offices.cancel")}
                            </Button>
                            <Button type="submit" disabled={!canSave}>
                                {isSaving ? <Spinner animation="border" size="sm" /> : t("pages.OrganizationSettings.offices.save")}
                            </Button>
                        </div>
                    </Form>
                )}
            </Card.Body>
        </Card>
    );
};

export default OfficeLocationsCard;
